// Style
import mainStyle from "./mainStyle.module.scss";

// Packages
import React, { useState, useEffect, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

// MUI
import { useTheme } from "@mui/material/styles";
import {
  Box,
} from "@mui/material";

// Components
import TableField from "components/Table";
import GlobalAlert from "components/Notification";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import RemovalModal from "./Modal/RemovalModal";
import CreateTemplate from "./components/CreateTemplate";
import { Loading } from 'components/Loading';

// IMG
import { ReactComponent as CreateIcon } from "assets/images/icons/icn_table_cell_Add.svg";
import { ReactComponent as CreateHoverIcon } from "assets/images/icons/icn_table_cell_Add_hover.svg";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

// Helper
import { getMainSendRequestParams, getInitTableData } from "./helper";

// Const
import { defaultAlertInfo, getMainTableColumns, defaultActions, defaultIsHover } from "./const";

const ConfigProvisioning = ({
  showEditGeneral
}) => {
  const [uuids] = useState(() => ({
    read: uuidv4(),
    update: uuidv4(),
    remove: uuidv4(),
  }));

  const theme = useTheme();
  const columnHelper = createColumnHelper();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();

  const [alertInfo, setAlertInfo] = useState(structuredClone(defaultAlertInfo));
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  const [actions, setActions] = useState(defaultActions);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isHover, setIsHover] = useState(defaultIsHover);

  const onPlayOrPause = ({
    activeState
  }) => {
    setIsFetching(true);
    setSelectedConfig(prevConfig => {
      return {
        ...prevConfig,
        activeState
      }
    })

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "update" });
    const payload = {
      data: {
        ...selectedConfig,
        activeState,
      }
    };

    sendRequest(
      apiUrl,
      uuids.update,
      requestParams,
      payload
    );
  }

  const onRemoveConfirm = () => {
    if (!selectedConfig?.id) return;

    setIsFetching(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "delete" });
    const payload = {
      data: {
        ids: [selectedConfig.id]
      }
    };

    sendRequest(
      apiUrl,
      uuids.remove,
      requestParams,
      payload
    );
  }

  const currentTableInstance = useTableInstance({
    tableName: "ConfigProvisioning",
    data: tableData,
    columns: getMainTableColumns({
      auth,
      columnHelper,
      permissions: auth.permissions,
      theme,
      mainTableCallbacks: {
        onPlay: config => {
          setSelectedConfig(config);
          setActions({ ...defaultActions, isPlay: true });
        },
        onPause: config => {
          setSelectedConfig(config);
          setActions({ ...defaultActions, isPause: true });
        },
        onEdit: config => {
          setSelectedConfig(config);
          setActions({ ...defaultActions, isOpenCreateTemplate: true })
        },
        onRemove: config => {
          setSelectedConfig(config);
          setActions({ ...defaultActions, isRemove: true });
        }
      }
    }),
    enableSorting: true,
    enableColumnOrder: false,
    enableRowSelection: true,
    enablePagination: true,
  });

  const fetchMainTableData = () => {
    setIsFetching(true);

    if (isAuthReady) {
      const { apiUrl, requestParams } = getMainSendRequestParams({ auth });

      sendRequest(
        apiUrl,
        uuids.read,
        requestParams
      );
    }
  }

  useEffect(() => {
    fetchMainTableData();
  }, [isAuthReady]);

  useEffect(() => {
    if (actions.isPlay) {
      onPlayOrPause({ activeState: true });
    } else if (actions.isPause) {
      onPlayOrPause({ activeState: false });
    } else if (actions.isRemoveConfirm) {
      onRemoveConfirm();
    } else if (actions.isSave) {
      fetchMainTableData();
      setActions(defaultActions);
      setSelectedConfig(null);
    } else if (actions.isOpenCreateTemplate) {
      // Nothing
    } else if (actions.isRemove) {
      // Nothing
    }
  }, [actions])

  useEffect(() => {
    if (!dataMap?.[uuids.read]) return;

    const response = getData(uuids.read);
    const responseData = response?.data ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to get the config provisioning list.",
        });
      } else {

        const initTableData = getInitTableData({
          responseData
        });

        setTableData(initTableData);
      }

      setIsFetching(false);
    }
  }, [dataMap?.[uuids.read]]);

  useEffect(() => {
    if (!dataMap?.[uuids.update]) return;

    const response = getData(uuids.update);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to update the config provisioning.",
        });
      } else {
        let msgs = {
          severity: "",
          title: "",
          description: ""
        }

        if (selectedConfig?.activeState) {
          msgs.severity = "success";
          msgs.title = "Active";
          msgs.description = "This template has been activated";
        } else {
          msgs.severity = "warning";
          msgs.title = "Inactive";
          msgs.description = "This template has been deactivated";
        }

        setAlertInfo({
          open: true,
          severity: msgs.severity,
          message: (
            <Box>
              <Box
                sx={{
                  fontSize: theme.typography.customFontSize[3],
                  fontWeight: theme.typography.customFontWeight[3],
                  pb: 1
                }}
              >
                {msgs.title}
              </Box>
              <Box>{msgs.description}</Box>
            </Box>
          )
        });

        fetchMainTableData();
      }

      setIsFetching(false);
      setSelectedConfig(null);
    }
  }, [dataMap?.[uuids.update]]);

  useEffect(() => {
    if (!dataMap?.[uuids.remove]) return;

    const response = getData(uuids.remove);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to remove the config provisioning.",
        });
      } else {
        fetchMainTableData();
      }

      setActions(defaultActions);
      setSelectedConfig(null);
      setIsFetching(false);
    }
  }, [dataMap?.[uuids.remove]]);

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  return (
    <>
      {isLoading && <Loading />}

      <Box sx={{ padding: "20px 30px" }}>
        {
          !actions.isOpenCreateTemplate &&
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                height: "80px"
              }}
            >
              <OutlineRectangleButton
                sx={{ whiteSpace: "nowrap", minWidth: "160px" }}
                onMouseEnter={() => setIsHover({ ...defaultIsHover, create: true })}
                onMouseLeave={() => setIsHover({ ...defaultIsHover, create: false })}
                startIcon={isHover?.create ? <CreateHoverIcon /> : <CreateIcon />}
                width="160px"
                height="35px"
                onClick={() => setActions({ ...defaultActions, isOpenCreateTemplate: true })}
              >
                Create Template
              </OutlineRectangleButton>
            </Box>

            <TableField
              {...currentTableInstance}
              handleRowOnClick={() => { }}
            />
          </Box>
        }

        {
          actions.isOpenCreateTemplate &&
          <CreateTemplate
            {...{
              dataMap,
              getData,
              sendRequest,
              alertInfo,
              setAlertInfo,
              actions,
              setActions,
              selectedConfig,
              setSelectedConfig,
              showEditGeneral,
            }}
          />
        }
      </Box>

      <RemovalModal
        {...{
          isOpenRemovalModal: actions.isRemove,
          selectedConfig,
          removeCallbacks: {
            removeCancel: () => {
              setSelectedConfig(null);
              setActions({ ...defaultActions, isRemove: false });
            },
            removeConfirm: () => {
              setActions({ ...defaultActions, isRemoveConfirm: true });
            },
          },
        }}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo(structuredClone(defaultAlertInfo))}
      />
    </>
  );
};

export default ConfigProvisioning;
